import React from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTasks } from "@fortawesome/free-solid-svg-icons";
import useOnboardingStepParam from "../../helpers/hooks/useOnboardingStepParam";
import {
  ONBOARDING_STEP_BY_KEY,
  ONBOARDING_TOTAL_STEPS,
} from "./onboardingSteps";

/**
 * HO-HU-36: recordatorio de que se llego a este modulo desde la guia de
 * primeros pasos, con la instruccion del paso a la vista.
 *
 * Se monta en cada modulo de destino declarando que pasos atiende (`steps`), y
 * no muestra nada si el usuario entro por su cuenta o si el paso de la URL es
 * de otro modulo. Al cerrarlo se saca el query param de la URL sin recargar,
 * asi no vuelve a aparecer si el usuario sigue trabajando en la pantalla.
 */
const OnboardingBanner = ({ steps, action }) => {
  const history = useHistory();
  const { pathname, state } = useLocation();
  const activeKey = useOnboardingStepParam();

  if (!activeKey || !steps.includes(activeKey)) {
    return null;
  }

  const step = ONBOARDING_STEP_BY_KEY[activeKey];

  if (!step) {
    return null;
  }

  // Se conserva el `state` de la navegacion: hay modulos (gastos ordinarios)
  // que lo necesitan para saber que tipo mostrar, y perderlo al cerrar el
  // banner dejaria la pantalla vacia.
  const dismiss = () => history.replace({ pathname, state });

  return (
    <Alert color="info" className="shadow-sm" toggle={dismiss}>
      <h5 className="alert-heading fs--1 mb-1">
        <FontAwesomeIcon icon={faTasks} className="mr-2" />
        Paso {step.position} de {ONBOARDING_TOTAL_STEPS}: {step.title}
      </h5>
      <p className="fs--1 mb-2">{step.description}</p>
      <div className="d-flex align-items-center flex-wrap">
        {action && (
          <Button
            color="info"
            size="sm"
            className="mr-3"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        )}
        <Button
          color="link"
          size="sm"
          className="pl-0"
          onClick={() => history.push("/dashboard/app")}
        >
          Volver a Primeros pasos
        </Button>
      </div>
    </Alert>
  );
};

OnboardingBanner.propTypes = {
  /** Claves de paso que atiende este modulo. */
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * Atajo opcional a la accion principal del paso, para no obligar al usuario
   * a buscarla entre los botones de la pantalla. Es un atajo y no un
   * automatismo a proposito: hay pasos con mas de un camino valido (cargar por
   * Excel o uno a uno) y abrir uno solo de entrada esconde el otro.
   */
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
};

export default OnboardingBanner;
